import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from "react-router-dom";
import axios from 'axios';
import { Form, Formik } from 'formik';
import { get } from 'lodash';
import { defineMessages } from 'react-intl';
import styled from 'styled-components';
import * as Yup from 'yup';
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";

import { 
  Box, 
  Card, 
  // Seo, 
  Flex, 
  // Link, 
  Text, 
  Button, 
  Input,
} from '@components';
import { Hero, HeroWrapper, TopRight } from '@layouts/Signup';
import { getError } from '@utils/helpers';
import Logo from '../../static/images/tb-os-logo.svg' ;

const LogoContainer = styled.div`
  display: flex;
`;

const messages = defineMessages({
  title: 'Try TeamBuildr OS for free',
  description:
    'It takes 5 minutes. Manage your memberships, scheduling and payments all in one place.',
  metaTitle: 'Create your studio',
  metaDescription:
    'Setup your studio within 5 minutes, no strings attached. Experience the most effortless studio management software.',
  unknownError: 'An unknown error has occured.',
  firstNameLabel: 'First name',
  lastNameLabel: 'Last name',
  phoneLabel: 'Phone number',
  phonePlaceholder: 'Phone',
  emailLabel: 'Email',
  studioNameLabel: 'Business name',
  submit: 'Create your studio',
  bottomText: 'No upfront payment or credit card required.',
  haveAccount: 'Already using TeamBuildr OS?',
  loginButton: 'Login'
});

const CustomLabel = styled.label`
  letter-spacing: -0.02em;
  color: ${p => p.theme.colors.grey700};
  font-size: 16px;
  line-height: 150%;
  font-weight: 800;
  margin-bottom: 8px;
  display: block;
`;

const GOOGLE_RECAPTCHA_KEY = '6Ld93LglAAAAAHLEehcWY-pnfPfsKXRDLLLROJQg';

axios.defaults.withCredentials = true;

const FormSchema = Yup.object().shape({
  firstName: Yup.string().required(),
  lastName: Yup.string().required(),
  email: Yup.string()
    .email()
    .required(),
  studioName: Yup.string().required(),
  phone: Yup.number().required(),
});

const Signup = () => {
  // const intl = useIntl();
  const signup = useRef();
  const [generalError, setGeneralError] = useState(null);

  if (typeof window !== 'undefined') {
    window.onSubmit = recaptchaToken => {
      if (signup.current) {
        signup.current(recaptchaToken);
      }
    };
  }

  const [country, setCountry] = useState("1"); // default to US

  useEffect(() => {
    // Google Tag Manager - inserted at the very top of <head>
    const gtmScript = document.createElement('script');
    gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-PMWNF4N');`;
    // Insert as the first child of <head> to ensure highest priority
    document.head.insertBefore(gtmScript, document.head.firstChild);

    // Google Tag Manager noscript fallback - inserted immediately after opening <body>
    const gtmNoscript = document.createElement('noscript');
    gtmNoscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-PMWNF4N" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    // Insert as the first child of <body> to ensure it's immediately after opening tag
    document.body.insertBefore(gtmNoscript, document.body.firstChild);

    if (!window.grecaptcha) {
      const script = document.createElement('script');
      script.async = true;
      script.defer = true;
      script.src = 'https://www.google.com/recaptcha/api.js';
      document.body.appendChild(script);
    }

    // Cleanup function to remove GTM scripts when component unmounts
    return () => {
      if (gtmScript.parentNode) {
        document.head.removeChild(gtmScript);
      }
      if (gtmNoscript.parentNode) {
        document.body.removeChild(gtmNoscript);
      }
    };
  }, []);

  const handleSubmit = (values, { setSubmitting }) => {
    signup.current = async recaptchaToken => {
      signup.current = undefined;

      try {
        values.phone = `+${country}${values.phone}`;
        const res = await axios.post(`${process.env.REACT_APP_NODE_API_URL}/studios`, {
          recaptchaToken,
          ...values
        });

        const startOnboardingUrl = get(res.data, 'startOnboardingUrl');
        if (!startOnboardingUrl) {
          throw new Error(messages.unknownError);
        }

        window.location.href = startOnboardingUrl;
      } catch (err) {
        setGeneralError(getError(err));
        setSubmitting(false);

        if (window.grecaptcha) {
          window.grecaptcha.reset();
        }
      }
    };

    if (window.grecaptcha) {
      window.grecaptcha.execute();
    }
  };

  const handlePrivacyLinkClick = (e) => {
    e.preventDefault();
    window.open('https://www.teambuildr.com/privacy-policy');
  };

  const handleTermsLinkClick = (e) => {
    e.preventDefault();
    window.open('https://www.teambuildr.com/terms-of-use');
  };

  const handleLoginClick = (e) => {
    e.preventDefault();
    window.location.href = '/login';
  };


  return (
    <>
      {/* <Seo
        title={intl.formatMessage(messages.metaTitle)}
        description={intl.formatMessage(messages.metaDescription)}
      /> */}

      <HeroWrapper>
        <Hero>
          <Box 
            width="100%" maxWidth="520px" px={[0, '20px']} 
            style={{ display: "flex", flexDirection: "column", alignItems: "center"}}
          >
            <Flex flexDirection="column" alignItems="center">
              {/* <Link to="/">
                <Text color="grey800">
                  <svg
                    viewBox="0 0 118 24"
                    height="20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.2 11.81c1.5-.9 2.16-2.64 2.16-5.07 0-3.54-2.28-5.52-5.64-5.52H1.49c-.36 0-.54.18-.54.54v20.7c0 .36.18.54.54.54h7.47c3.93 0 5.91-1.89 5.91-5.67 0-2.76-.93-4.68-2.67-5.52zm-7.65-1.74V4.82h3.78c1.59 0 2.43.84 2.43 2.52 0 1.77-.48 2.73-2.43 2.73H4.55zm0 9.33v-5.73h3.78c1.89 0 2.94.96 2.94 3.06 0 2.01-.93 2.67-2.94 2.67H4.55zm16.47 0V1.67c0-.3-.15-.45-.45-.45h-2.7c-.3 0-.45.15-.45.45v20.88c0 .3.15.45.45.45H29.9c.3 0 .45-.15.45-.45v-2.7c0-.3-.15-.45-.45-.45h-8.88zM39.197.89c-3.45 0-6.3 3.21-6.3 7.17v8.16c0 3.96 2.85 7.17 6.3 7.17h1.56c3.45 0 6.3-3.21 6.3-7.17V8.06c0-3.96-2.85-7.17-6.3-7.17h-1.56zm0 18.9c-1.74 0-2.7-1.53-2.7-3.33V7.82c0-1.8.96-3.33 2.7-3.33h1.56c1.77 0 2.7 1.53 2.7 3.33v8.64c0 1.8-.93 3.33-2.7 3.33h-1.56zM55.63.89c-3.27 0-5.91 2.4-5.91 5.91 0 3.48 2.4 6.18 5.97 6.81l1.74.3c2.1.36 2.55 1.68 2.55 3.33 0 1.56-1.17 2.58-2.7 2.58H55.9c-1.41 0-2.58-1.02-2.64-2.46l-.03-.87c0-.3-.15-.45-.45-.45h-2.73c-.3 0-.45.15-.45.45v.87c0 3.54 3.09 6.06 6.3 6.06h1.38c3.33 0 6.3-2.52 6.3-6.15 0-3.78-2.7-6.39-6-6.96l-1.74-.3c-2.25-.39-2.52-1.77-2.52-3.06 0-1.26.84-2.46 2.31-2.46h1.56c1.35 0 2.52 1.14 2.52 2.7v.57c0 .3.15.45.45.45h2.73c.3 0 .45-.15.45-.45v-.57c0-3.66-3-6.3-6.15-6.3h-1.56zm16.386 0c-3.27 0-5.91 2.4-5.91 5.91 0 3.48 2.4 6.18 5.97 6.81l1.74.3c2.1.36 2.55 1.68 2.55 3.33 0 1.56-1.17 2.58-2.7 2.58h-1.38c-1.41 0-2.58-1.02-2.64-2.46l-.03-.87c0-.3-.15-.45-.45-.45h-2.73c-.3 0-.45.15-.45.45v.87c0 3.54 3.09 6.06 6.3 6.06h1.38c3.33 0 6.3-2.52 6.3-6.15 0-3.78-2.7-6.39-6-6.96l-1.74-.3c-2.25-.39-2.52-1.77-2.52-3.06 0-1.26.84-2.46 2.31-2.46h1.56c1.35 0 2.52 1.14 2.52 2.7v.57c0 .3.15.45.45.45h2.73c.3 0 .45-.15.45-.45v-.57c0-3.66-3-6.3-6.15-6.3h-1.56zm16.805 0c-3.45 0-6.3 3.21-6.3 7.17v8.16c0 3.96 2.85 7.17 6.3 7.17h1.56c3.45 0 6.3-3.21 6.3-7.17V8.06c0-3.96-2.85-7.17-6.3-7.17h-1.56zm0 18.9c-1.74 0-2.7-1.53-2.7-3.33V7.82c0-1.8.96-3.33 2.7-3.33h1.56c1.77 0 2.7 1.53 2.7 3.33v8.64c0 1.8-.93 3.33-2.7 3.33h-1.56zm24.654-18.57c-.3 0-.51.15-.6.45l-4.29 14.55-4.26-14.55c-.09-.3-.3-.45-.6-.45h-3.6c-.3 0-.45.15-.45.45v20.88c0 .3.15.45.45.45h2.7c.3 0 .45-.15.45-.45V8.9l3.99 13.65c.09.3.3.45.57.45h1.53c.27 0 .48-.15.57-.45l3.99-13.65v13.65c0 .3.15.45.45.45h2.7c.3 0 .45-.15.45-.45V1.67c0-.3-.15-.45-.45-.45h-3.6z"
                      fill="currentColor"
                    />
                  </svg>
                </Text>
              </Link> */}
            </Flex>

            <LogoContainer>
              <img src={Logo} alt="Gym Studio Logo" />
            </LogoContainer>
            <Card px={['24px', '48px']} my="32px" textAlign='left'>
              <Text
                fontSize={['24px', '28px']}
                lineHeight="100%"
                as="h1"
                fontWeight="800"
                letterSpacing="-0.02em"
                mb={['16px', '32px']}
              >
                {messages.title}
              </Text>
              <Text
                mb="24px"
                lineHeight="150%"
                fontSize={['16px', '18px']}
                fontWeight="600"
                as="p"
                color="grey600"
              >
                {messages.description}
              </Text>
              <Formik
                validateOnBlur
                validateOnChange={false}
                initialValues={{
                  firstName: '',
                  lastName: '',
                  email: '',
                  studioName: '',
                  phone: '',
                }}
                validationSchema={FormSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting, values, setFieldValue }) => (
                  <Form autoComplete="off">
                    <Flex flexDirection={['column', 'row']} mb="16px">
                      <Input
                        name="firstName"
                        label={messages.firstNameLabel}
                        mb={['16px', 0]}
                        mr={[0, '16px']}
                        width="100%"
                      />
                      <Input
                        name="lastName"
                        label={messages.lastNameLabel}
                        width="100%"
                      />
                    </Flex>
                    <Input
                      name="email"
                      label={messages.emailLabel}
                      mb="16px"
                    />

                    <CustomLabel>Phone number</CustomLabel>

                    <div style={{ display: 'flex', flexDirection: 'row'}}>
                      <PhoneInput
                        country={"us"} // initial value
                        value={country}
                        containerStyle={{ width: '20%', height: 50, marginRight: 10 }}
                        inputStyle={{ width: '30%', height: 50 }}
                        onChange={(code) => setCountry(code.toString())}
                      />

                      <Input
                        style={{ width: 300 }}
                        name="phone"
                        placeholder={"Phone number"}
                        mb="16px"
                        />
                    </div>

  
                    <Input
                      name="studioName"
                      label={messages.studioNameLabel}
                    />

                    <Box mt={['24px', '32px']}>
                      {!!generalError && (
                        <Box mb={['24px', '32px']}>
                          <Text color="danger">{generalError}</Text>
                        </Box>
                      )}
                      <div
                        id="g-recaptcha"
                        className="g-recaptcha"
                        data-sitekey={GOOGLE_RECAPTCHA_KEY}
                        data-callback="onSubmit"
                        data-size="invisible"
                      />
                      <Button
                        width="100%"
                        px="30px"
                        type="submit"
                        loading={isSubmitting}
                        disabled={isSubmitting}
                      >
                        {messages.submit}
                      </Button>
                    </Box>
                  </Form>
                )}
              </Formik>

              <Flex
                flexDirection="column"
                alignItems="center"
                textAlign="center"
              >
                <Text
                  mt="24px"
                  lineHeight="150%"
                  fontSize="12px"
                  fontWeight="600"
                  color="grey500"
                  as="p"
                >
                  {messages.bottomText}
                </Text>
              </Flex>
            </Card>

            <Flex
              flexDirection="column"
              alignItems="center"
              textAlign="center"
              maxWidth="270px"
              mx="auto"
            >
            <Text
              lineHeight="150%"
              fontSize="12px"
              fontWeight="800"
              color="grey600"
              as="p"
            >
              By signing up for TeamBuildr OS you agree to the{' '}
              <RouterLink style={{ color: '#0068A5', textDecoration: 'underline' }} onClick={handlePrivacyLinkClick}>Privacy Policy</RouterLink> and{' '}
              <RouterLink style={{ color: '#0068A5', textDecoration: 'underline' }} onClick={handleTermsLinkClick}>Terms of Use</RouterLink>.
            </Text>
            </Flex>
          </Box>
        </Hero>

        <TopRight>
          <Text
            lineHeight="19px"
            fontSize="16px"
            fontWeight="600"
            color="black"
            mr={[0, '24px']}
            mb={['16px', 0]}
          >
            {messages.haveAccount}
          </Text>
          <RouterLink onClick={handleLoginClick}>
            <Button color="login">
                {messages.loginButton}
            </Button>
          </RouterLink>
        </TopRight>
      </HeroWrapper>
    </>
  );
};

export default Signup;
